import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { WarningAmberRounded, ArrowDropUp } from '@mui/icons-material';
import { RiskLevel } from '../../../../Services/API/riskProfileApi';
import { getRiskLevelMeta } from './RiskProfileHelpers';

interface RiskLevelBarProps {
  riskLevels: RiskLevel[];
  currentValue: number;
}

export const RiskLevelBar: React.FC<RiskLevelBarProps> = ({ riskLevels, currentValue }) => {
  const theme = useTheme();
  const isDarkTheme = theme.palette.mode === 'dark';
  const borderColor = isDarkTheme ? '#474E59' : 'lightgray';
  const currentLevel = getRiskLevelMeta(riskLevels, currentValue);
  
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <WarningAmberRounded fontSize="small" />
        <Typography fontWeight="bold">Risk Level</Typography>
      </Box>

      <Box
        sx={{
          position: 'relative',
          borderRadius: '8px',
          overflow: 'hidden',
          display: 'flex',
          height: '18px',
          backgroundColor: 'transparent',
          width: '100%',
          border: `4px solid ${borderColor}`
        }}
      >
        {riskLevels.map((level) => (
          <Box
            key={level.id}
            sx={{
              flex: 1,
              bgcolor: level.color,
              position: 'relative',
              transition: 'flex 0.3s'
            }}
          />
        ))}

        <Box
          sx={{
            position: 'absolute',
            left: `${(riskLevels.findIndex(l => l.id === currentLevel.id) / riskLevels.length) * 100}%`,
            top: '-6px',
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderBottom: '6px solid white'
          }}
        />
      </Box>

      <Box
        sx={{
          mt: 1,
          position: 'relative',
          height: '24px'
        }}
      >
        <ArrowDropUp
          fontSize="large"
          sx={{
            position: 'absolute',
            top: -27,
            left: `${(riskLevels.findIndex(l => l.id === currentLevel.id) + 0.5) / riskLevels.length * 100}%`,
            transform: 'translateX(-50%)'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: `${(riskLevels.findIndex(l => l.id === currentLevel.id) + 0.5) / riskLevels.length * 100}%`,
            transform: 'translateX(-50%)',
            bgcolor: currentLevel.colorSecondary,
            padding: '2px 10px',
            borderRadius: '20px',
            color: currentLevel.color,
            border: `1px solid ${currentLevel.color}`
          }}
        >
          <Typography fontWeight="bold" variant="body2">
            {currentLevel.name}
          </Typography>
        </Box>
      </Box>

      <Typography sx={{ mt: 2 }}>{currentLevel.description}</Typography>
    </Box>
  );
};